/**
 * Multi-supplier picker (prototype `openLineSuppliers` / `bulkAssignMulti`).
 *
 * The point of the screen: one item goes to MANY suppliers at once, each as a competing
 * quote. For a single line it shows the item's candidate suppliers with their last known
 * price and stock; in bulk mode it lists the whole supplier master, since the selected
 * lines are different items.
 *
 * A supplier can be created inline here without leaving the flow (contract §4).
 *
 * ---- New-supplier bar: LIVE since 2026-09-03 (point 28) ----
 *
 * `sales_person.create_supplier` exists and works; the fields below were disabled behind `A6`
 * waiting for it. It DISCARDS the email, though — accepted, 200, stored nowhere (probed, see
 * `lib/api/createSupplier.ts`) — so the bar says so rather than letting a purchaser type an
 * address, watch the supplier appear, and assume the RFQ has somewhere to go.
 */

import { useState } from 'react'
import { Modal, SelectAllToggle } from '../../components/Modal'
import { Button, Placeholder } from '../../components/ui'
import { Money } from '../../components/Money'
import { useCreateSupplier, usePurchaseHistory, useSuppliers } from '../../lib/api/useSuppliers'
import { activeSupplierCodes, useSupplierEmailsByCode, useSuppliersForItem } from '../../lib/api/useItems'
import { suppliersForGroup, useItemGroupSuppliers } from '../../lib/api/useItemGroupSuppliers'
import { bulkCandidateCodes, emailLookupCodes, indicativePrice, MAX_EMAIL_LOOKUPS } from '../../lib/rfq'
import { SEARCH_DEBOUNCE_MS, useDebounced } from '../../lib/useDebounced'
import { useToast } from '../../components/Toast'
import { isLiveFeature, NOT_AVAILABLE_YET } from '../../lib/api/liveAvailability'
import type { Item, Money as MoneyType } from '../../lib/types'

function NewSupplierBar({ onCreated }: { onCreated: (code: string) => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const toast = useToast()
  const available = isLiveFeature('supplierCreate')

  const create = useCreateSupplier({
    onSuccess: (supplier) => {
      toast(`Supplier created: ${supplier.name}`)
      setName('')
      setEmail('')
      // The Link value, not the display name just typed — see `bulkCandidateCodes`' header.
      onCreated(supplier.code)
    },
  })

  return (
    <div
      className="pad"
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'flex-end',
        borderBottom: '1px solid var(--line)',
        background: 'var(--bg)',
      }}
    >
      <label className="ef" style={{ flex: 1 }}>
        <span>New supplier name</span>
        <input
          value={name}
          placeholder="e.g. Fujairah Marine"
          disabled={!available}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="ef" style={{ flex: 1 }}>
        <span>Email</span>
        <input
          value={email}
          placeholder="rfq@…"
          disabled={!available}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <Button
        size="sm"
        variant="primary"
        disabled={!available || !name.trim() || create.isPending}
        onClick={() => create.mutate({ name: name.trim(), email: email.trim() })}
      >
        ＋ Create &amp; add
      </Button>
      {!available ? (
        <div style={{ fontSize: 10.5, color: 'var(--muted)' }}>{NOT_AVAILABLE_YET}</div>
      ) : create.error ? (
        <div className="warnflag">{create.error.message}</div>
      ) : email.trim() !== '' ? (
        /* Said BEFORE the click, not after. The endpoint accepts the address and stores nothing,
           so a purchaser who types one and sees "Supplier created" has every reason to believe the
           RFQ can reach them — and it cannot. */
        <div style={{ fontSize: 10.5, color: 'var(--warn)', maxWidth: 190 }}>
          The address isn't saved yet — add it in the supplier master.
        </div>
      ) : null}
    </div>
  )
}

/**
 * What we know about a supplier's RFQ address (fix list point 5).
 *
 * `unchecked` is a real and common state, not a placeholder: bulk mode lists up to 200 suppliers
 * and `items.get_supplier_emails` takes one supplier per call, so looking every row up would be
 * 200 requests to open a dialog. Only the rows that matter are resolved — see `emailFor`.
 */
type EmailState =
  | { status: 'unchecked' }
  | { status: 'pending' }
  | { status: 'none' }
  | { status: 'ok'; address: string }

/** One row of the checklist; the history line is context for choosing, not a price to use. */
function SupplierRow({
  code,
  label,
  checked,
  onToggle,
  item,
  historyPrice,
  email,
}: {
  /** `Supplier.code` — the identity ticking this row toggles. */
  code: string
  /** Display name only — never compared or saved. */
  label: string
  checked: boolean
  onToggle: () => void
  item?: Item
  historyPrice?: MoneyType | null
  /** Where this supplier's RFQ would go — see `EmailState`. */
  email: EmailState
}) {
  const contract = item?.contracts.find((c) => c.supplier === code)
  // This supplier's own contract rate if there is one, else the item's indicative price —
  // the prototype always shows a per-unit figure to choose against.
  const unitPrice = contract?.rate ?? indicativePrice(item)

  return (
    <label className="supcheck">
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <span>
        <b>{label}</b>
        <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
          {unitPrice ? (
            <>
              {contract ? 'contract ' : ''}
              <Money value={unitPrice} />/{item?.base_uom} ·{' '}
            </>
          ) : null}
          {/*
            Both figures the API gives — on the shelf, and how much is already promised elsewhere.
            `on_shelf` is optional, and unknown stock reads "—", never "0": a zero would claim the
            item is out of stock when nothing has answered.
          */}
          {item
            ? `stock ${item.on_shelf ?? item.on_hand ?? '—'} ${item.base_uom}` +
              (item.reserved ? ` (${item.reserved} reserved)` : '')
            : 'supplier master'}
          {historyPrice ? (
            <>
              {' · '}
              <span style={{ color: 'var(--ok)' }}>
                history <Money value={historyPrice} />
              </span>
            </>
          ) : null}
        </div>
        {/*
          The address, on its own line because it is long and a purchaser reads it as one value.

          FOUR states, deliberately not collapsed into "address or not". `unchecked` renders
          NOTHING: in bulk mode most rows are never looked up (see `emailFor`), and printing "no
          email on file" for them would send someone to fix supplier records that are perfectly
          fine. `pending` must not read as `none` for the same reason.
        */}
        {email.status === 'unchecked' ? null : (
          <div style={{ fontSize: 11.5, marginTop: 1 }}>
            {email.status === 'pending' ? (
              <span style={{ color: 'var(--muted)' }}>looking up address…</span>
            ) : email.status === 'ok' ? (
              <span
                className="mono"
                style={{ color: 'var(--muted)' }}
                title={`RFQs for ${label} go to ${email.address}`}
              >
                {email.address}
              </span>
            ) : (
              <span
                style={{ color: 'var(--warn)' }}
                title="An RFQ cannot be sent to this supplier until an address exists on their record"
              >
                no email on file — cannot be sent an RFQ
              </span>
            )}
          </div>
        )}
      </span>
    </label>
  )
}

export function SupplierPickerModal({
  title,
  intro,
  item,
  initialSelected,
  company,
  current = [],
  onRemove,
  onClear,
  onSave,
  onClose,
}: {
  title: string
  intro: string
  /** Set for a single line; omitted in bulk mode, where the lines are different items. */
  item?: Item
  initialSelected: string[]
  /** The enquiry's company — scopes `get_suppliers`. Falls back to the session's operating company. */
  company?: string
  /**
   * Bulk mode only (Akhil, 2026-09-15): the suppliers ALREADY on the selected lines, so the same
   * dialog that adds to many lines at once can take one off them all, or clear them all.
   */
  current?: { supplier: string; lines: number }[]
  onRemove?: (supplier: string) => void
  onClear?: () => void
  onSave: (suppliers: string[]) => void
  onClose: () => void
}) {
  const [selected, setSelected] = useState<string[]>(initialSelected)
  /**
   * Search runs SERVER-SIDE. The bench holds ~2,700 suppliers and `masters.get_suppliers` caps at
   * 200 per request, so the unsearched list was never the master — it was the first 200 of it,
   * with no way to reach a supplier whose name sorted later. Debounced so a typed word is one
   * request rather than five.
   */
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search, SEARCH_DEBOUNCE_MS)
  const allSuppliers = useSuppliers(debouncedSearch, company)
  const history = usePurchaseHistory(item?.code)
  // Fetched only now, because this component only exists in the tree while the picker is open —
  // opening it (the "＋ suppliers" button, or bulk "Assign suppliers…") IS the on-demand trigger;
  // there is no separate per-row fetch to guard against (see `useItems.ts`'s own header on why
  // this can't be a per-catalogue-row read). `item` is unset in bulk mode, which the hook's own
  // `enabled: !!itemCode` already turns into a no-op query rather than something to branch on here.
  const suppliersForItem = useSuppliersForItem(item?.code ?? '')
  // One request for the WHOLE item-group→supplier mapping, cached for the session — not per item.
  const groupSuppliers = useItemGroupSuppliers()

  // Display names only — every value in `candidates`/`selected` is a `Supplier.code`.
  //
  // Built from BOTH sources, because the master list is now search-narrowed: an item-group
  // candidate whose name doesn't match the current search term is absent from `allSuppliers`, and
  // labelling it by its raw code would make the single-item list unreadable the moment someone
  // typed in the box. `get_suppliers_for_item` carries `supplier_name` for exactly its own rows.
  const nameByCode = new Map<string, string>()
  for (const row of suppliersForItem.data ?? []) nameByCode.set(row.supplier, row.supplier_name)
  for (const s of allSuppliers.data?.data ?? []) nameByCode.set(s.code, s.name)
  const labelFor = (code: string) => nameByCode.get(code) ?? code

  // For one item, offer its candidate suppliers first; in bulk, the whole master — both by
  // CODE (see `bulkCandidateCodes`'s header). Every source below is code-keyed, so the union
  // never mixes identities. Only ~3.5% of item groups are mapped, and the Frappe team have
  // confirmed that is permanent ("some major item groups may be empty") — so an item with NO
  // candidates is the normal case, not a broken fetch. `emptyReason` below says which.
  //
  // TWO SOURCES, group first. An item has no suppliers of its own: `Item Group Supplier` hangs
  // off `Item Group`, so the candidates are the GROUP's. `data_ops.get_item_groups_with_suppliers`
  // returns that whole mapping in one request and is resolved here against `item.group`, which we
  // already hold. `items.get_suppliers_for_item` does the same join server-side and stays as the
  // fallback — it is a sibling of `items.get_blanket_orders`, which returns `[]` for items that
  // demonstrably have active orders (`D1`), so it is not something to depend on alone.
  const groupCandidates = suppliersForGroup(groupSuppliers.data, item?.group)
  const perItemCandidates = suppliersForItem.data ? activeSupplierCodes(suppliersForItem.data) : []
  const itemCandidates = groupCandidates.length > 0 ? groupCandidates : perItemCandidates
  const candidates = item
    ? [...new Set([...itemCandidates, ...selected])]
    : bulkCandidateCodes(allSuppliers.data?.data ?? [])

  // The server caps at 200 per request; `total` is what it says exists. Surfaced rather than
  // hidden — see the count line in the render below.
  const shown = allSuppliers.data?.data.length ?? 0
  const total = allSuppliers.data?.total ?? shown
  const truncated = total > shown

  /**
   * Why the list is empty — the three cases must not look alike on screen.
   *
   * `null` while anything is still in flight, so a slow fetch never renders as "none mapped".
   */
  const lookupsPending = suppliersForItem.isPending || groupSuppliers.isPending
  const lookupsFailed = suppliersForItem.isError && groupSuppliers.isError
  const emptyReason: string | null =
    candidates.length > 0 || lookupsPending
      ? null
      : lookupsFailed
        ? 'Could not load suppliers for this item — the lookup failed. Try again, or create one above.'
        : item
          ? `No suppliers are mapped to this item's group${item.group ? ` (${item.group})` : ''}. Assign some in the item group, or create a supplier above.`
          : 'No suppliers yet — create one above.'

  const toggle = (code: string) =>
    setSelected((current) => (current.includes(code) ? current.filter((s) => s !== code) : [...current, code]))

  // `usePurchaseHistory` entries are keyed by `Supplier.code` (`PurchaseHistoryEntry.supplier`,
  // `src/lib/types.ts`), the SAME identity `candidates`/`selected` use — matched directly by
  // code, never by the display label (fix round: comparing against `labelFor(code)` here matched
  // nothing, because the mock used to echo the supplier's raw NAME on this endpoint while every
  // other identity on this screen is a code; see `mock-server/bosun/handlers/items.ts`'s
  // `get_purchase_history`, now fixed to join to the code like its neighbours).
  const historyFor = (code: string) => history.data?.find((h) => h.supplier === code)?.price ?? null

  /*
    The address each supplier's RFQ would go to (fix list point 5).

    ---- Which rows are looked up, and why not all of them ----

    `items.get_supplier_emails` takes ONE supplier per call. For a single item that is fine — the
    candidates are the item group's mapped suppliers, a handful. In BULK mode `candidates` is the
    whole 200-row supplier page, and looking all of them up would fire 200 requests to open a
    dialog, for addresses nobody asked about.

    `emailLookupCodes` resolves EVERY supplier on screen, plus the selection first. The address is
    what a purchaser chooses by — a supplier without one cannot be sent an RFQ at all — so making
    it conditional on ticking, or on the list being short, hid it in exactly the cases it was
    needed. Addresses fill in progressively (one call each, six at a time); a row says "looking up
    address…" until its own lands, and a row past the safety ceiling renders nothing rather than
    claiming an absence nobody verified — see `EmailState`.

    Deliberately NOT `Supplier.primary_email`: that field is `''` live even for suppliers this
    method resolves to a real address (defect B3, `useSuppliers.ts`'s `toSupplier`).
  */
  /*
    Addresses already on the supplier list cost nothing — `masters.get_suppliers` now carries
    `primary_email` for some suppliers (1 of 200 today, more as `data_ops.import_supplier_email`
    backfills). Those are used directly, and only the REST are looked up one at a time.

    So the request count falls as the backfill progresses, without this code changing again.
  */
  const listedEmail = new Map<string, string>()
  for (const s of allSuppliers.data?.data ?? []) if (s.email) listedEmail.set(s.code, s.email)
  const emailCodes = emailLookupCodes(
    candidates.filter((c) => !listedEmail.has(c)),
    selected.filter((c) => !listedEmail.has(c)),
  )
  const emailLookedUp = new Set(emailCodes)
  /** Only when the list exceeds the safety ceiling — never in practice, since the supplier page
   *  itself caps at 200. Said on the count line rather than silently. */
  const emailsCapped = candidates.length > MAX_EMAIL_LOOKUPS
  // Per-supplier cache, not per-set — otherwise every tick in bulk mode refetches every address
  // already resolved. See `useSupplierEmailsByCode`.
  const emails = useSupplierEmailsByCode(emailCodes)
  const emailFor = (code: string): EmailState => {
    // Free, off the list we already hold.
    const listed = listedEmail.get(code)
    if (listed) return { status: 'ok', address: listed }
    if (!emailLookedUp.has(code)) return { status: 'unchecked' }
    if (emails.isPending(code)) return { status: 'pending' }
    const address = emails.byCode.get(code)
    return address ? { status: 'ok', address } : { status: 'none' }
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <SelectAllToggle
            checked={candidates.length > 0 && candidates.every((c) => selected.includes(c))}
            onChange={(checked) => setSelected(checked ? candidates : [])}
          />
          <span style={{ flex: 1 }} />
          <Button variant="primary" onClick={() => onSave(selected)}>
            {item ? 'Save suppliers' : 'Apply to selected'}
          </Button>
        </>
      }
    >
      {current.length > 0 && (onRemove || onClear) ? (
        <div className="pad" style={{ borderBottom: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Already on the selected lines:</span>
            {current.map((c) => (
              <span key={c.supplier} className="pill" title={`On ${c.lines} of the selected line(s)`}>
                {nameByCode.get(c.supplier) ?? c.supplier}
                <span style={{ color: 'var(--muted)', marginLeft: 4 }}>×{c.lines}</span>
                {onRemove ? (
                  <button
                    type="button"
                    className="linkish"
                    style={{ marginLeft: 6, color: 'var(--danger)' }}
                    title={`Remove ${c.supplier} from every selected line`}
                    onClick={() => onRemove(c.supplier)}
                  >
                    ✕
                  </button>
                ) : null}
              </span>
            ))}
            <span style={{ flex: 1 }} />
            {onClear ? (
              <Button size="sm" variant="danger" onClick={onClear} title="Take every supplier off every selected RFQ line">
                Clear all suppliers
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="pad" style={{ color: 'var(--muted)', fontSize: 12.5 }}>
        {intro}
      </div>

      <NewSupplierBar onCreated={(code) => setSelected((current) => [...new Set([...current, code])])} />

      <div className="pad" style={{ borderBottom: '1px solid var(--line)' }}>
        <input
          type="search"
          value={search}
          placeholder="Search all suppliers by name…"
          style={{ width: '100%' }}
          onChange={(e) => setSearch(e.target.value)}
        />
        {/*
          Says WHICH suppliers are on screen, because "the list" is not the master: the server
          returns at most 200 rows per request against ~2,700 suppliers. Without this line an
          absent supplier reads as "we don't have them" rather than "type their name".
        */}
        {!item ? (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
            {allSuppliers.isFetching
              ? 'Searching…'
              : truncated
                ? `Showing ${shown} of ${total} — search to narrow.`
                : search.trim() !== '' && shown === 0
                  ? 'No supplier matches that name.'
                  : `Showing all ${shown}.`}
            {/* Never a silent cap: without this line the missing addresses read as "these
                suppliers have none" rather than "we did not ask". */}
            {emailsCapped ? ` Addresses shown for the first ${MAX_EMAIL_LOOKUPS} — search to narrow.` : ''}
          </div>
        ) : null}
      </div>

      <div className="mb">
        {allSuppliers.isPending && !item ? <Placeholder>Loading suppliers…</Placeholder> : null}
        {item && lookupsPending ? <Placeholder>Loading suppliers for this item…</Placeholder> : null}
        {emptyReason ? (
          <div className="pad" style={{ color: lookupsFailed ? 'var(--warn)' : 'var(--muted)' }}>
            {emptyReason}
          </div>
        ) : null}
        {candidates.map((code) => (
          <SupplierRow
            key={code}
            code={code}
            label={labelFor(code)}
            checked={selected.includes(code)}
            onToggle={() => toggle(code)}
            item={item}
            historyPrice={historyFor(code)}
            email={emailFor(code)}
          />
        ))}
      </div>
    </Modal>
  )
}
